import { NgModule } from '@angular/core';
import { Router } from '@angular/router';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { environment } from '@src/environments';
import {
  provideInstrumentation,
  provideOpenTelemetryService,
  WebOpenTelemetryService,
} from '@vanguard/vg-otel-js/angular';
import {
  ATTR_VG_SERVICE_APP_PREFIX_TRANSITIONAL,
  BaggageConstantsBaggageProvider,
} from '@vanguard/vg-otel-js/common';
import { SpanProcessorType } from '@vanguard/vg-otel-js/web';

import {
  createBaggageConfig,
  ignoreUrls,
  otelAppPrefix,
  otelServiceName,
  otelSpanProcessorType,
  propagateTraceHeaderCorsUrls as defaultPropagateTraceHeaderCorsUrls,
  routePatternFromSnapshot,
} from './otel.config';

const propagateTraceHeaderCorsUrls =
  environment.propagateTraceHeaderCorsUrls ?? defaultPropagateTraceHeaderCorsUrls;

const deploymentConfig = environment.otelCollectorConfig;

@NgModule({
  providers: [
    provideInstrumentation(new FetchInstrumentation({ ignoreUrls, propagateTraceHeaderCorsUrls })),
    provideInstrumentation(
      new XMLHttpRequestInstrumentation({ ignoreUrls, propagateTraceHeaderCorsUrls }),
    ),
    provideInstrumentation(new DocumentLoadInstrumentation()),
    // OpenTelemetry service provider
    provideOpenTelemetryService({
      // not SSR-safe, need SSR-safe WindowRef factory if we want to use this in an SSR build
      _window: window,

      // Use SIMPLE to reduce risk of dropped spans (matches example).
      // You can switch to BATCH later if perf/network becomes a concern.
      spanProcessorFactoryOrType: SpanProcessorType[otelSpanProcessorType],

      deploymentConfig,

      resourceAttributes: {
        [ATTR_SERVICE_NAME]: otelServiceName,
        [ATTR_VG_SERVICE_APP_PREFIX_TRANSITIONAL]: otelAppPrefix,
      },

      baggageProvider: {
        useFactory: (router: Router) => {
          return BaggageConstantsBaggageProvider.for(
            createBaggageConfig(
              () => window.location.href,
              () => routePatternFromSnapshot(router.routerState.snapshot.root),
            ),
          );
        },
        deps: [Router],
      },
    }),
  ],
})
export class OtelModule {
  // Force eager instantiation of WebOpenTelemetryService.
  // Angular creates the module instance after registering all providers,
  // so this constructor injection triggers the full OTEL initialization chain:
  // WebOpenTelemetryService → TracerFactory → WebTracerProvider.register()
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(_otelService: WebOpenTelemetryService) {}
}
